import { TextField, ValidationErrors } from "@compass/core";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker as MuiDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopDatePickerProps as MuiDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker";
import MomentUtils from "@date-io/moment";
import { useEffect, useRef } from "react";
import { parseDateInput } from "../common/date-input";
import { displayErrorMessages, getErrorMessages } from "../common/input-functions";

export type DatePickerBaseProps = Omit<MuiDatePickerProps<Date, Date>, "value" | "onChange" | "renderInput"> & {
  label?: string;
  value?: any;
  onChange?: (date: any | undefined, keyboardInputValue?: string) => void;
  errors?: ValidationErrors | string[];
  required?: boolean;
  size?: "small" | "medium" | undefined;
  disabled?: boolean;
  autoFocus?: boolean;
};

export const DatePickerBase = ({
  label,
  value,
  onChange,
  size = "medium",
  errors,
  required,
  disabled,
  autoFocus,
  views = ["month", "year", "day"],
  inputFormat = "MM/DD/YYYY",
  disableMaskedInput = true,
  ...rest
}: DatePickerBaseProps) => {
  const errorMsg = getErrorMessages(errors);
  const hasError = errorMsg?.length > 0 ? true : false;
  const typedDateCommitTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(typedDateCommitTimeout.current);
    },
    []
  );

  const handleChange = (dateValue, keyboardInputValue?: string) => {
    clearTimeout(typedDateCommitTimeout.current);

    if (keyboardInputValue !== undefined) {
      if (!keyboardInputValue) {
        onChange?.(null);
        return;
      }

      const trimmedKeyboardInput = keyboardInputValue.trim();
      const typedDate = parseDateInput(trimmedKeyboardInput);

      if (typedDate.isValid()) {
        const formattedDate = typedDate.format("YYYY-MM-DD");

        if (
          /(?:\/|-|\.|\s)\d{2}$/.test(trimmedKeyboardInput) ||
          /^\d{6}$/.test(trimmedKeyboardInput) ||
          /^\d{4}[\/\.\-\s]+\d{1,2}[\/\.\-\s]+\d{1,2}$/.test(trimmedKeyboardInput)
        ) {
          typedDateCommitTimeout.current = setTimeout(() => onChange?.(formattedDate), 500);
        } else {
          onChange?.(formattedDate);
        }
      }

      return;
    }

    if (!dateValue) {
      onChange?.(null);
      return;
    }

    if (dateValue.isValid()) {
      onChange?.(dateValue.format("YYYY-MM-DD"));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        label={label}
        value={value || null} // || null to prevent auto prefill today's date when adding a new listItem: why?
        onChange={handleChange}
        disabled={disabled}
        views={views}
        inputFormat={inputFormat}
        disableMaskedInput={disableMaskedInput}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            required={required}
            variant={"outlined"}
            error={params.error ? params.error : hasError}
            helperText={displayErrorMessages(errorMsg)}
            size={size}
            autoComplete="off"
            autoFocus={autoFocus}
          />
        )}
        {...rest}
      />
    </LocalizationProvider>
  );
};
